import { Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, LinearProgress, Paper, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import NotFound from "../../../app/errors/NotFound";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import AppTimeline, { Align, TimeLineElement } from "../../../app/components/AppTimeLine";
import { clientOrdersSelectors, fetchClientOrderAsync } from "./clientOrdersSlice";
import ClientHelpInOrderChat from "./ClientHelpInOrderChat";
import { Order, OrderState } from "../../../app/models/order";
import agent from "../../../app/api/agent";
import { currencyFormat, formatToLocalDate, formatToLocalTime, getOrderStatusColor, getOrderStatusTitle } from "../../../app/util/util";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import AppReturn from "../../../app/components/AppReturn";
import ClientOrderScanUploadForm from "./ClientOrderScanUploadForm";
import DownloadAndPreviewButtons from "../../../app/components/AppDownloadAndPreviewButtons";
import { ParticipantsList } from "../../../app/components/Order/OrderParticipantsList";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

export default function ClientOrderDetails() {
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const order = useAppSelector(state => clientOrdersSelectors.selectById(state, id!));
    const { status: orderStatus } = useAppSelector(state => state.clientOrders);
    const [timeLineElements, setTimeLineElements] = useState<TimeLineElement[]>([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const isMobile = useMediaQuery('(max-width: 900px)');
    const [open, setOpen] = useState(false);
    const [isGeneratingTest, setIsGeneratingTest] = useState(false);
    const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
    const [isAIGenerating, setIsAIGenerating] = useState(order?.isTestGenerationInProgress || false);

    useEffect(() => {
        if (!order) dispatch(fetchClientOrderAsync(parseInt(id!)))
    }, [id, dispatch, order])

    // Inicjalizacja stanu generowania AI gdy order się załaduje
    useEffect(() => {
        if (order?.isTestGenerationInProgress && !isAIGenerating) {
            setIsAIGenerating(true);
        }
    }, [order?.isTestGenerationInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

    // Polling statusu generowania co 20 sekund (tylko dla klienta - bez szczegółów, tylko odświeżenie)
    useEffect(() => {
        if (!isAIGenerating || !order) {
            return;
        }

        const checkStatus = async () => {
            try {
                // Odśwież order aby sprawdzić czy generowanie się zakończyło
                await dispatch(fetchClientOrderAsync(order.publicId));
                
                // Jeśli flaga się zmieniła na false, zakończ polling
                // (to zostanie obsłużone przez useEffect powyżej przy następnym renderze)
            } catch (error) {
                console.error('Błąd podczas sprawdzania statusu generowania:', error);
            }
        };

        // Sprawdzaj status co 20 sekund
        const intervalId = setInterval(checkStatus, 20000);

        // Cleanup
        return () => clearInterval(intervalId);
    }, [isAIGenerating, order, dispatch]);

    // Synchronizuj stan z orderem
    const updateAIGeneratingState = useCallback(() => {
        if (order && !order.isTestGenerationInProgress && isAIGenerating) {
            setIsAIGenerating(false);
            toast.success('Twoje zamówienie zostało przetworzone!');
        }
    }, [order, isAIGenerating]);

    useEffect(() => {
        updateAIGeneratingState();
    }, [updateAIGeneratingState]);

    function getStatusIcon(currentState: number) {
        switch (currentState) {
            case 0:
                return <HourglassTopIcon />;
            case 1:
                return <HourglassTopIcon />;
            case 2:
                return <DoneIcon />;
            case 3:
                return <DoneIcon />;
            case 4:
                return <HelpOutlineIcon />;
            case 5:
                return <CancelIcon />;
            case 6:
                return <HourglassTopIcon />;
            case 7:
                return <DoneIcon />;
            default:
                return <ErrorIcon />;
        }
    }

    useEffect(() => {
        const fetchHistory = async () => {
            if (order) {
                setLoading(true);
                try {
                    const historyOfOrder: Order[] = await agent.Client.Orders.getHistoryOfOrder(order.publicId);
                    const timelineData: TimeLineElement[] = [];
                    const statusTracker: { [key: number]: boolean } = {};
                    let isTestAvailableDisplayed = false; // Tracker for the first occurrence of isTestAvailable

                    historyOfOrder.forEach(h => {
                        const isFirstOccurrence = !statusTracker[h.orderState];
                        statusTracker[h.orderState] = true;

                        // Check for the first occurrence of isTestAvailable == true
                        let subtitle = isFirstOccurrence ? getOrderStatusTitle(h.orderState) : 'Edytowano';
                        if (h.isTestAvailable && !isTestAvailableDisplayed) {
                            subtitle = 'Udostępniono testy';
                            isTestAvailableDisplayed = true;
                        }

                        timelineData.push({
                            dateAndTimeToDisplay: new Date(h.updatingDateTime).toLocaleString(),
                            align: Align.Left,
                            header: h.updatingUserIsAdmin == true ? 'Administrator' : 'Klient',
                            subtitle: subtitle,
                            icon: getStatusIcon(h.orderState),
                            iconColor: getOrderStatusColor(h.orderState)
                        });
                    });

                    setTimeLineElements(timelineData);
                } catch (error) {
                    console.error("Failed to fetch investment history:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchHistory();
    }, [order]);

    if (orderStatus.includes('pending')) return <LoadingComponent />

    if (!order) return <NotFound />

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        try {
            await agent.Client.Orders.cancelOrder(order.publicId);
            toast.success('Zamówienie zostało anulowane.');
            dispatch(fetchClientOrderAsync(parseInt(id!)));
        } catch (error) {
            console.error('Wystąpił błąd podczas anulowania zamówienia:', error);
            toast.error('Nie udało się anulować zamówienia.');
        } finally {
            setIsCancelling(false);
            handleCloseDialog();
        }
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const downloadBlob = (blob: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    async function handleGenerateCertificates(orderId: number) {
        setIsGeneratingCertificate(true);
        try {
            const response = await agent.GenerateCertificate.generateCertificate(orderId);

            if (response.data instanceof Blob) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                downloadBlob(blob, `Certyfikaty_${orderId}.pdf`);
            } else {
                throw new Error('Nieprawidłowa odpowiedź z serwera');
            }
        } catch (error: any) {
            console.error('Błąd podczas generowania certyfikatów:', error);

            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas generowania certyfikatów');
            }
        } finally {
            setIsGeneratingCertificate(false);
        }
    }

    // Funkcja do generowania testu PDF z pytaniami
    const handleGenerateTestPdf = async () => {
        if (!order) return;

        setIsGeneratingTest(true);
        try {
            await agent.Client.Orders.generateTestPdf(order.publicId);
            toast.success('Test został pomyślnie wygenerowany i pobrany');
        } catch (error) {
            console.error('Błąd podczas generowania testu PDF:', error);
            toast.error('Nie udało się wygenerować testu PDF');
        } finally {
            setIsGeneratingTest(false);
        }
    };

    // Zmodyfikowana funkcja renderTestDownloadSection
    const renderTestDownloadSection = () => {
        // Najpierw sprawdzamy czy zamówienie zostało anulowane
        if (order.orderState === 5) {
            return (
                <Alert severity="error" sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                    Zamówienie zostało anulowane. Dostęp do testów egzaminacyjnych nie jest już możliwy.
                </Alert>
            );
        }
        
        // Jeśli nie jest anulowane, sprawdzamy czy testy są dostępne
        if (!order.isTestAvailable) {
            return (
                <Alert severity="info" sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                    W tym miejscu możliwe będzie pobranie testów egzaminacyjnych.
                </Alert>
            );
        }

        if (order.isGraphical && order.packageTestPath) {
            // Testy graficzne - pobieranie przez API proxy (prawidłowa nazwa pliku)
            return (
                <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                    <CardHeader title='Pobierz arkusze egzaminacyjne'
                        titleTypographyProps={{
                            sx: { fontWeight: 'bold', color: 'secondary' }
                        }} />
                    <Divider />
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box display="flex" gap={1} justifyContent="flex-start" alignItems="center">
                                    <LoadingButton 
                                        variant="contained"
                                        startIcon={<CloudDownloadIcon />}
                                        onClick={handleGenerateTestPdf}
                                        loading={isGeneratingTest}
                                        disabled={isAIGenerating}
                                        sx={{ minWidth: 150 }}
                                    >
                                        Pobierz
                                    </LoadingButton>
                                    <Button
                                        variant="outlined"
                                        onClick={() => window.open(order.packageTestPath, '_blank')}
                                        disabled={isAIGenerating}
                                    >
                                        Podgląd
                                    </Button>
                                </Box>
                                <Typography variant="overline" sx={{ display: 'block', textAlign: 'left', mt: 1 }}>
                                    egzamin graficzny
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            );
        } else if (!order.isGraphical && order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}") {
            // Testy tekstowe - tylko generowanie PDF z questionsJson (gdy questionsJson istnieje)
            return (
                <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                    <CardHeader title='Pobierz arkusze egzaminacyjne'
                        titleTypographyProps={{
                            sx: { fontWeight: 'bold', color: 'secondary' }
                        }} />
                    <Divider />
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-start" alignItems="center">
                                    <LoadingButton 
                                        variant="contained"
                                        startIcon={<CloudDownloadIcon />}
                                        onClick={handleGenerateTestPdf}
                                        loading={isGeneratingTest}
                                        disabled={isAIGenerating}
                                        sx={{ minWidth: 150 }}
                                    >
                                        Pobierz test
                                    </LoadingButton>
                                </Box>
                                <Typography variant="overline" sx={{ display: 'block', textAlign: 'left', mt: 1 }}>
                                    egzamin tekstowy
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            );
        }
        
        // Przypadek gdy order.isTestAvailable = true, ale brak informacji o typie testu
        return (
            <Alert severity="warning" sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                Testy są dostępne, ale wystąpił problem z ich formatem. Prosimy o kontakt z administratorem.
            </Alert>
        );
    };

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Zamówienie #" + order.publicId} backPath="/client/orders" />

            {isMobile ? (
                <Button
                    onClick={handleOpenDialog}
                    sx={{ width: '100%', mb: 2 }}
                    color="error"
                    variant='outlined'
                    size='large'
                    disabled={isAIGenerating || (order.orderState == 0 ? false : order.orderState == 1 ? false : true)}
                >
                    Anuluj zamówienie
                </Button>
            ) : (
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        onClick={handleOpenDialog}
                        sx={{ m: 2 }}
                        color="error"
                        variant='outlined'
                        size='large'
                        disabled={isAIGenerating || (order.orderState == 0 ? false : order.orderState == 1 ? false : true)}
                    >
                        Anuluj zamówienie
                    </Button>
                </Box>
            )}

            {/* Alert informujący o przetwarzaniu zamówienia przez AI */}
            {isAIGenerating && (
                <Alert 
                    severity="info" 
                    sx={{ 
                        p: 3, 
                        mb: 2,
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Twoje zamówienie jest przetwarzane
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Proces ten zajmuje zazwyczaj około minuty. Prosimy o cierpliwość - strona zostanie 
                            automatycznie zaktualizowana po zakończeniu przetwarzania.
                        </Typography>
                        <LinearProgress />
                    </Box>
                </Alert>
            )}

            {/* Alert dla zamówień zatwierdzonych bez skanów */}
            {order.orderState === OrderState.APPROVED_WITHOUT_SCANS && (
                <Alert 
                    severity="warning" 
                    sx={{ 
                        p: 3, 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                    action={
                        <Button
                            color="warning"
                            variant="contained"
                            size="medium"
                            onClick={() => setOpen(true)}
                        >
                            Dodaj skany
                        </Button>
                    }
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Egzamin został zatwierdzony - wymagane przesłanie skanów
                        </Typography>
                        <Typography variant="body2">
                            Twój egzamin został zatwierdzony przez administratora, jednak brakuje przesłanych skanów 
                            rozwiązanych arkuszy egzaminacyjnych. Prosimy o jak najszybsze przesłanie skanów, 
                            aby ukończyć proces i uzyskać dostęp do certyfikatów.
                        </Typography>
                    </Box>
                </Alert>
            )}

            <Grid container>
                <Grid item md={7} sm={12}>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Podstawowe informacje'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.customeClientServiceName}
                                    </Typography>
                                    <Typography variant="overline">
                                        nazwa wskazana przez Klienta
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.serviceName}
                                    </Typography>
                                    <Typography variant="overline">
                                        bazowa nazwa
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {formatToLocalDate(order.orderDateTime.toString())} {formatToLocalTime(order.orderDateTime.toString())}
                                    </Typography>
                                    <Typography variant="overline">
                                        data i godzina złożenia zamówienia
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.preposedDateTime
                                            ? formatToLocalDate(order.preposedDateTime.toString())
                                            : 'Brak proponowanej daty'}
                                    </Typography>
                                    <Typography variant="overline">
                                        proponowana data realizacji egzaminu
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.trainingProgram}
                                    </Typography>
                                    <Typography variant="overline">
                                        wskazany program szkolenia (jeżeli dotyczy)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.clientComment}
                                    </Typography>
                                    <Typography variant="overline">
                                        komentarz Klienta
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Cena'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {currencyFormat(order.totalDiscountedPrice)}
                                    </Typography>
                                    <Typography variant="overline">
                                        wartość zamówienia
                                    </Typography>
                                    <Typography variant="h5">
                                        {currencyFormat(order.unitDiscountedPrice)}
                                    </Typography>
                                    <Typography variant="overline">
                                        cena jedostkowa
                                    </Typography>
                                    <Typography variant="h5">
                                        {order.totalItem}
                                    </Typography>
                                    <Typography variant="overline">
                                        liczba osób
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {order.bizInInvoiceURL && (
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title={`Faktura nr ${order.accountNumber}`}
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={() => window.open(order.bizInInvoiceURL, '_blank')}
                                        sx={{ mr: 1 }}
                                    >
                                        Otwórz faktury
                                    </Button>
                                    <Typography variant="overline" sx={{ display: 'block', mt: 1 }}>
                                        Kliknij aby pobrać faktury w formacie PDF
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    )}
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Dane klienta'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.userDisplayName}
                                    </Typography>
                                    <Typography variant="overline">
                                        nazwa Klienta
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.userEmail}
                                    </Typography>
                                    <Typography variant="overline">
                                        email
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.userPhone}
                                    </Typography>
                                    <Typography variant="overline">
                                        telefon kontaktowy
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.trainingProgram}
                                    </Typography>
                                    <Typography variant="overline">
                                        wskazany program szkolenia (jeżeli dotyczy)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.clientComment}
                                    </Typography>
                                    <Typography variant="overline">
                                        komentarz Klienta
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Dane Płatnika'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.payerClientName}
                                    </Typography>
                                    <Typography variant="overline">
                                        nazwa Płatnika
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.payerClientAddress}
                                    </Typography>
                                    <Typography variant="overline">
                                        adres Płatnika
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {order.payerClientNIP ? order.payerClientNIP : order.payerNIP}
                                    </Typography>
                                    <Typography variant="overline">
                                        NIP Płatnika
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={5} >
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Historia'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                        <CircularProgress />
                                    </Box>
                                ) : <AppTimeline elements={timeLineElements} />}
                            </Grid>
                        </Grid>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Korespondencja'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                <ClientHelpInOrderChat orderId={parseInt(id!)} />
                            </Grid>
                        </Grid>
                    </Card>
                    {order.solvedTestsPaths != null && order.solvedTestsPaths != '' ?
                        (
                            <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                                <CardHeader title='Lista uczestników'
                                    titleTypographyProps={{
                                        sx: { fontWeight: 'bold', color: 'secondary' }
                                    }} />
                                <Divider />
                                <CardContent>
                                    <ParticipantsList benefitients={order.benefitients} />
                                </CardContent>
                                {order.isExamApproved && (
                                    <>
                                        <Divider />
                                        <CardContent>
                                            <Tooltip title={
                                                isAIGenerating 
                                                    ? 'Zamówienie jest przetwarzane - proszę poczekać.'
                                                    : order.orderState === 5 
                                                        ? 'Zamówienie anulowane - certyfikaty niedostępne' 
                                                        : 'Pobierz certyfikaty'
                                            }>
                                                <span>
                                                    <LoadingButton
                                                        loading={isGeneratingCertificate}
                                                        disabled={isAIGenerating || order.orderState === 5}
                                                        onClick={() => handleGenerateCertificates(order.publicId)}
                                                        variant='outlined'
                                                        color='success'
                                                    >
                                                        Pobierz certyfikaty
                                                    </LoadingButton>
                                                </span>
                                            </Tooltip>
                                        </CardContent>
                                    </>
                                )}
                            </Card>
                        ) : ''}

                    {/* Zmodyfikowana sekcja pobierania testów */}
                    {renderTestDownloadSection()}

                    {/* Sekcja z przesłanymi skanami */}
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Przesłane skany'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            {order.solvedTestsPaths ? (
                                <DownloadAndPreviewButtons order={{
                                    path: order.solvedTestsPaths
                                }} count={true} disabled={isAIGenerating} />
                            ) : (
                                <Alert severity="info">
                                    Nie przesłano jeszcze skanów egzaminów.
                                </Alert>
                            )}
                        </CardContent>
                        <Divider />
                        <Tooltip title={
                            isAIGenerating
                                ? 'Zamówienie jest przetwarzane - proszę poczekać.'
                                : (order.isExamApproved && order.orderState !== OrderState.APPROVED_WITHOUT_SCANS && order.orderState !== OrderState.SCANS_ADDED_AFTER_APPROVAL)
                                    ? 'Testy zostały zatwierdzone - nie można dodać nowych.' 
                                    : order.orderState === 5
                                        ? 'Zamówienie zostało anulowane - nie można dodać nowych skanów.'
                                        : (order.orderState === OrderState.APPROVED_WITHOUT_SCANS || order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL)
                                            ? 'Dodaj skany egzaminacyjne'
                                            : ''
                        }>
                            <span>
                                <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        sx={{ height: '40px' }}
                                        variant='outlined'
                                        onClick={handleOpen}
                                        size="small"
                                        title='Załącz'
                                        color={(order.orderState === OrderState.APPROVED_WITHOUT_SCANS || order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL) ? 'warning' : 'success'}
                                        disabled={isAIGenerating || (order.isExamApproved && order.orderState !== OrderState.APPROVED_WITHOUT_SCANS && order.orderState !== OrderState.SCANS_ADDED_AFTER_APPROVAL && order.orderState !== OrderState.SCANS_ADDED) || order.orderState === 5}
                                    >
                                        {(order.orderState === OrderState.APPROVED_WITHOUT_SCANS || order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL) ? 'Dodaj skany' : 'Załącz'}
                                    </Button>
                                </CardContent>
                            </span>
                        </Tooltip>
                    </Card>
                </Grid>
            </Grid>
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Anulowanie zamówienia"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {order.isPaid ? 'Twoje zamówienie zostało już opłacone, czy chcesz rozpocząć proces zapytania o anulowanie zamówienia?'
                            : 'Czy na pewno chcesz anulować to zamówienie?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Box width='100%' display="flex" justifyContent='space-between'>
                        <Button onClick={handleCloseDialog} variant='outlined' sx={{ ml: 2 }}>Nie</Button>
                        <LoadingButton
                            onClick={handleConfirmCancel}
                            autoFocus
                            variant='outlined'
                            color='error'
                            loading={isCancelling}
                            sx={{ mr: 2 }}
                        >
                            Tak
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog open={open} onClose={handleClose}>
                <DialogContent>
                    <ClientOrderScanUploadForm order={order} cancelUpload={handleClose} />
                </DialogContent>
            </Dialog>
        </>
    )
}